import React, { useMemo } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import SearchableSelect from '../common/SearchableSelect';
import AgChart from '../dashboard2/AgChart';
import ILocalAGGrid, { getDefaultColDef } from '../grid/iLocalAGGrid';
import { ErrorIconCell } from './CampaignErrorDialog';

const OUTCOME_KEYS = {
  delivered: 'Campaigns.OutcomeDelivered',
  partial: 'Campaigns.OutcomePartial',
  failed: 'Campaigns.OutcomeFailed',
  scheduled: 'Campaigns.StatusScheduled',
  paused: 'Campaigns.StatusPaused',
  sending: 'Campaigns.StatusSending',
  draft: 'Campaigns.StatusDraft',
  cancelled: 'Campaigns.StatusCancelled',
};

const OUTCOME_DEFAULTS = {
  delivered: 'Delivered',
  partial: 'Partial',
  failed: 'Failed',
  scheduled: 'Scheduled',
  paused: 'Paused',
  sending: 'Sending',
  draft: 'Draft',
  cancelled: 'Cancelled',
};

const OUTCOME_FILLS = {
  delivered: '#16a34a',
  partial: '#d97706',
  failed: '#dc2626',
  scheduled: '#2563eb',
  paused: '#7c3aed',
  sending: '#0891b2',
  draft: '#64748b',
  cancelled: '#94a3b8',
};

const formatWhen = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const OutcomeCell = (params) => {
  const outcome = (params.data && params.data.outcome) || '';
  return (
    <span className={'mcc-campaign-status is-' + (outcome || 'draft')}>
      {outcome === 'failed' ? <ErrorOutline fontSize="inherit" /> : null}
      {params.value || '—'}
    </span>
  );
};

const HistoryProgressCell = (params) => {
  const row = params.data || {};
  const failed = row.outcome === 'failed' || row.status === 'failed';
  const sent = Number(row.sent_count || 0);
  const skipped = Number(row.skipped_count || 0);
  const total = Number(row.recipient_count || 0);
  const processed = failed ? Math.max(sent + skipped, total) : sent + skipped;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  return (
    <div
      className={
        'mcc-campaign-progress-cell' + (failed ? ' is-failed' : '')
      }
    >
      {total > 0 ? (
        <LinearProgress variant="determinate" value={pct} />
      ) : null}
      <span className={'mcc-campaign-progress' + (failed ? ' is-failed' : '')}>
        {failed ? <ErrorOutline fontSize="inherit" /> : null}
        {params.value || '—'}
      </span>
    </div>
  );
};

const CampaignAnalytics = ({ t, analytics, loading, days, onDaysChange, onViewError }) => {
  const totals = (analytics && analytics.totals) || {};
  const rates = (analytics && analytics.rates) || {};
  const history = (analytics && analytics.history) || [];

  const kpis = [
    {
      id: 'campaigns',
      label: t('Campaigns.KpiCampaigns', { defaultValue: 'Campaigns' }),
      value: String(totals.campaigns || 0),
    },
    {
      id: 'delivered',
      label: t('Campaigns.KpiDelivered', { defaultValue: 'Delivered' }),
      value: String(totals.delivered || 0),
    },
    {
      id: 'failed',
      label: t('Campaigns.KpiFailed', { defaultValue: 'Failed / partial' }),
      value: String((totals.failed || 0) + (totals.partial || 0)),
    },
    {
      id: 'messages',
      label: t('Campaigns.KpiMessages', { defaultValue: 'Messages sent' }),
      value: String(totals.messages_sent || 0),
    },
    {
      id: 'charged',
      label: t('Campaigns.KpiCharged', { defaultValue: 'Amount charged' }),
      value: totals.charged_label || '$0.00',
    },
  ];

  const spendOptions = useMemo(
    () => ({
      data: (analytics && analytics.spend_series) || [],
      height: 280,
      legend: { position: 'bottom' },
      series: [
        {
          type: 'area',
          xKey: 'day',
          yKey: 'charged',
          yName: t('Campaigns.ChartSpend', { defaultValue: 'Charged ($)' }),
          fill: '#00a7e1',
          stroke: '#0284c7',
          fillOpacity: 0.28,
          marker: { enabled: true, size: 4 },
        },
      ],
      axes: [
        { type: 'category', position: 'bottom', label: { rotation: -45 } },
        {
          type: 'number',
          position: 'left',
          nice: true,
          min: 0,
          label: {
            formatter: (params) =>
              `$${Number((params && params.value) || 0).toFixed(2)}`,
          },
        },
      ],
    }),
    [analytics, t]
  );

  const outcomeOptions = useMemo(
    () => {
      const series = ((analytics && analytics.outcome_series) || []).map(
        (item) => ({
          outcome: t(OUTCOME_KEYS[item.outcome] || 'Campaigns.Status', {
            defaultValue: OUTCOME_DEFAULTS[item.outcome] || item.outcome,
          }),
          count: item.count,
          key: item.outcome,
        })
      );
      return {
        data: series,
        height: 280,
        legend: { position: 'bottom' },
        series: [
          {
            type: 'pie',
            angleKey: 'count',
            legendItemKey: 'outcome',
            calloutLabelKey: 'outcome',
            innerRadiusRatio: 0.55,
            fills: series.map(
              (item) => OUTCOME_FILLS[item.key] || '#64748b'
            ),
            strokes: ['#fff'],
          },
        ],
      };
    },
    [analytics, t]
  );

  const channelOptions = useMemo(
    () => ({
      data: ((analytics && analytics.channel_series) || []).map((item) => ({
        channel:
          item.channel === 'email'
            ? t('Campaigns.Email', { defaultValue: 'Email' })
            : t('Campaigns.Sms', { defaultValue: 'SMS' }),
        sent: item.sent,
        skipped: item.skipped,
      })),
      height: 280,
      legend: { position: 'bottom' },
      series: [
        {
          type: 'bar',
          xKey: 'channel',
          yKey: 'sent',
          yName: t('Campaigns.ChartSent', { defaultValue: 'Messages sent' }),
          fill: '#00a7e1',
        },
        {
          type: 'bar',
          xKey: 'channel',
          yKey: 'skipped',
          yName: t('Campaigns.ChartSkipped', {
            defaultValue: 'Skipped',
          }),
          fill: '#94a3b8',
        },
      ],
      axes: [
        { type: 'category', position: 'bottom' },
        { type: 'number', position: 'left', nice: true, min: 0 },
      ],
    }),
    [analytics, t]
  );

  const defaultColDef = useMemo(() => getDefaultColDef({ minWidth: 110 }), []);

  const historyRows = useMemo(
    () =>
      history.map((row) => {
        const outcome = row.outcome || 'draft';
        return Object.assign({}, row, {
          outcome_label: t(OUTCOME_KEYS[outcome] || 'Campaigns.Status', {
            defaultValue: OUTCOME_DEFAULTS[outcome] || outcome,
          }),
          channel_label:
            row.channel === 'email'
              ? t('Campaigns.Email', { defaultValue: 'Email' })
              : t('Campaigns.Sms', { defaultValue: 'SMS' }),
          progress_label: `${row.sent_count || 0} / ${row.skipped_count || 0} / ${
            row.recipient_count || 0
          }`,
          error_label:
            row.error_label ||
            row.error ||
            (Number(row.skipped_count || 0) > 0 || row.outcome === 'failed'
              ? 'The message was not delivered. No detailed provider error was stored for this send.'
              : ''),
          when_at: row.sent_at || row.updated_at || row.created_on,
        });
      }),
    [history, t]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('Campaigns.Name', { defaultValue: 'Campaign name' }),
        minWidth: 160,
      },
      {
        field: 'location_name',
        colId: 'location_name',
        headerName: t('Campaigns.Location', { defaultValue: 'Location' }),
        minWidth: 150,
      },
      {
        field: 'channel_label',
        headerName: t('Campaigns.Channel', { defaultValue: 'Channel' }),
        minWidth: 100,
      },
      {
        field: 'outcome_label',
        headerName: t('Campaigns.Outcome', { defaultValue: 'Outcome' }),
        cellRenderer: OutcomeCell,
        minWidth: 130,
      },
      {
        field: 'progress_label',
        headerName: t('Campaigns.HistoryProgress', {
          defaultValue: 'Sent / skipped / recipients',
        }),
        cellRenderer: HistoryProgressCell,
        minWidth: 180,
      },
      {
        field: 'charged_label',
        headerName: t('Campaigns.Charged', { defaultValue: 'Charged' }),
        minWidth: 110,
      },
      {
        field: 'error_label',
        headerName: t('Campaigns.Error', { defaultValue: 'Error' }),
        cellRenderer: ErrorIconCell,
        cellRendererParams: {
          t,
          onViewError,
        },
        sortable: false,
        filter: false,
        minWidth: 88,
        maxWidth: 110,
      },
      {
        field: 'when_at',
        headerName: t('Campaigns.Updated', { defaultValue: 'Updated' }),
        valueFormatter: (params) => formatWhen(params.value),
        minWidth: 170,
      },
    ],
    [t, onViewError]
  );

  if (loading && !analytics) {
    return (
      <div className="mcc-inline-progress">
        <LinearProgress />
        <span>
          {t('Campaigns.BusyAnalytics', {
            defaultValue: 'Loading analytics…',
          })}
        </span>
      </div>
    );
  }

  return (
    <div className="campaign-analytics">
      <div className="campaign-analytics-toolbar">
        <div className="campaign-analytics-days">
          <SearchableSelect
            id="campaign-analytics-range"
            label={t('Campaigns.AnalyticsRange', { defaultValue: 'Range' })}
            items={[
              {
                id: '7',
                name: t('Campaigns.Last7', { defaultValue: 'Last 7 days' }),
              },
              {
                id: '30',
                name: t('Campaigns.Last30', { defaultValue: 'Last 30 days' }),
              },
              {
                id: '90',
                name: t('Campaigns.Last90', { defaultValue: 'Last 90 days' }),
              },
            ]}
            value={String(days || 30)}
            variant="outlined"
            fullWidth={false}
            disabled={!!loading}
            sx={{ minWidth: 220 }}
            onChange={(event) => {
              if (typeof onDaysChange === 'function') {
                onDaysChange(Number(event.target.value));
              }
            }}
          />
        </div>
      </div>
      <div className="campaign-kpi-grid">
        {kpis.map((item) => (
          <div key={item.id} className={'campaign-kpi is-' + item.id}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
      <p className="campaign-rate-card">
        {t('Campaigns.RateCard', {
          defaultValue:
            'Rate card: {{sms}} per SMS · {{email}} per email. Success {{success}}% · delivery {{delivery}}%.',
          sms: rates.sms_label || '$0.02',
          email: rates.email_label || '$0.01',
          success: totals.success_rate || 0,
          delivery: totals.delivery_rate || 0,
        })}
      </p>
      <div className="campaign-chart-grid">
        <div className="campaign-chart-card">
          <h3>
            {t('Campaigns.SpendChart', {
              defaultValue: 'Spend and messages over time',
            })}
          </h3>
          <AgChart
            options={spendOptions}
            height={280}
            fileName="campaign-spend"
          />
        </div>
        <div className="campaign-chart-card">
          <h3>
            {t('Campaigns.OutcomeChart', { defaultValue: 'Outcomes' })}
          </h3>
          <AgChart
            options={outcomeOptions}
            height={280}
            fileName="campaign-outcomes"
          />
        </div>
        <div className="campaign-chart-card">
          <h3>
            {t('Campaigns.ChannelChart', {
              defaultValue: 'SMS vs email',
            })}
          </h3>
          <AgChart
            options={channelOptions}
            height={280}
            fileName="campaign-channels"
          />
        </div>
      </div>
      <div className="campaign-history-card">
        <h3>
          {t('Campaigns.HistoryTitle', { defaultValue: 'Campaign history' })}
        </h3>
        <p className="campaign-form-hint">
          {t('Campaigns.HistoryHint', {
            defaultValue:
              'Charged is actual billed amount for delivered messages. Skipped includes missing phone/email and opt-outs.',
          })}
        </p>
        <div className="mcc-template-grid-wrap">
          <ILocalAGGrid
            className="ag-theme-alpine mcc-template-grid"
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="clientSide"
            rowData={historyRows}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 25]}
            rowHeight={44}
            overlayNoRowsMessage={t('Campaigns.Empty', {
              defaultValue: 'No campaigns yet.',
            })}
            getRowId={(params) => (params.data && params.data.id) || ''}
          />
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalytics;



// WEBPACK FOOTER //
// ./src/components/campaigns/CampaignAnalytics.js